import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormLabel } from "../../components/form-label/FormLabel";
import { flowService } from "../../services/flowService";
import { chatbotService } from "../../services/chatbotService";
import { tenantService } from "../../services/tenantService";
import { Flow, Tenant } from "../../types";

export default function FlowList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantIdParam = searchParams.get("tenantId") ?? "";

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  // Flow IDs referenced by at least one chatbot of the tenant. Flows outside this
  // set are orphans (no chatbot points to them). Derived FE-side: no BE endpoint.
  const [usedFlowIds, setUsedFlowIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tenantService
      .list()
      .then((res) => setTenants(res.data.data.tenants))
      .catch((e) => {
        console.error(e);
        setError("Errore nel caricamento dei tenant");
      });
  }, []);

  useEffect(() => {
    if (!tenantIdParam) {
      setFlows([]);
      setUsedFlowIds(new Set());
      return;
    }
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const [res, cbRes] = await Promise.all([
          flowService.list(tenantIdParam),
          chatbotService.list(tenantIdParam),
        ]);
        setFlows(res.data.data.flows);
        setUsedFlowIds(
          new Set(
            cbRes.data.data.chatbots
              .map((c) => c.flow_id)
              .filter((id): id is string => !!id)
          )
        );
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento dei flow");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tenantIdParam]);

  const handleDelete = async (flowId: string) => {
    if (!window.confirm("Eliminare questo flow?")) return;
    setDeleting(flowId);
    try {
      await flowService.delete(flowId);
      setFlows((prev) => prev.filter((f) => f.id !== flowId));
    } catch (e) {
      console.error(e);
      setError("Errore durante l'eliminazione del flow");
    } finally {
      setDeleting(null);
    }
  };

  const newFlowUrl = tenantIdParam
    ? `/chatbot/flows/new?tenantId=${tenantIdParam}`
    : "/chatbot/flows/new";

  const editFlowUrl = (flowId: string) =>
    tenantIdParam
      ? `/chatbot/flows/${flowId}?tenantId=${tenantIdParam}`
      : `/chatbot/flows/${flowId}`;

  const handleDuplicate = (flow: Flow) => {
    navigate(newFlowUrl, { state: { duplicateFrom: flow } });
  };

  return (
    <div className="container">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <FormLabel label="Lista Flow" cols={2} />
        <Button
          style={{ flex: "1 1 20%" }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(newFlowUrl)}
          disabled={!tenantIdParam}
        >
          Nuovo flow
        </Button>
      </Box>

      <Box sx={{ mb: 2, maxWidth: 320 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="tenant-filter">Tenant</InputLabel>
          <Select
            labelId="tenant-filter"
            label="Tenant"
            value={tenantIdParam}
            onChange={(e) =>
              setSearchParams(
                e.target.value ? { tenantId: e.target.value } : {},
              )
            }
          >
            <MenuItem value="">— seleziona tenant —</MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!tenantIdParam && (
        <Alert severity="info">
          Seleziona un tenant per visualizzare i flow.
        </Alert>
      )}

      {tenantIdParam && loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : tenantIdParam ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Canale</TableCell>
                <TableCell>Step</TableCell>
                <TableCell>Associazione</TableCell>
                <TableCell>Aggiornato il</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nessun flow trovato per questo tenant
                  </TableCell>
                </TableRow>
              )}
              {flows.map((f) => (
                <TableRow key={f.id} hover>
                  <TableCell>{f.name}</TableCell>
                  <TableCell>{f.channel}</TableCell>
                  <TableCell>{f.steps.length}</TableCell>
                  <TableCell>
                    {usedFlowIds.has(f.id) ? (
                      <Chip label="Associato" color="success" size="small" variant="outlined" />
                    ) : (
                      <Chip label="Non associato" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(f.updated_at).toLocaleDateString("it-IT")}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifica flow">
                      <IconButton
                        size="small"
                        onClick={() => navigate(editFlowUrl(f.id))}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Vedi esecuzioni">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(
                            `/chatbot/monitoring/conversations?tenantId=${tenantIdParam}&flowId=${f.id}`
                          )
                        }
                      >
                        <TimelineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplica flow">
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicate(f)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina flow">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleting === f.id}
                        onClick={() => handleDelete(f.id)}
                      >
                        {deleting === f.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </div>
  );
}
